import { gql } from '@apollo/client';

/* ── mutual fund research reports ───────────────────────────── */

/**
 * Published research reports. `archetype` selects the report template —
 * "GENERAL" is the one app_admin generates and reads.
 *
 * Pass `securitySymbols` to get one fund's reports, omit it for the catalogue.
 */
export const MF_REPORTS = gql`
  query MfReports(
    $securitySymbols: [String!]
    $archetype: String
    $limit: Int = 25
    $skip: Int = 0
    $includeHistory: Boolean = false
  ) {
    getMutualFundReports(
      securitySymbols: $securitySymbols
      archetype: $archetype
      limit: $limit
      skip: $skip
      includeHistory: $includeHistory
    ) {
      total
      items {
        id
        securitySymbol
        archetype
        fundName
        fundHouse
        reportUrl
        verified
        isLatest
        isComplete
        generatedAt
      }
    }
  }
`;

/**
 * The report's own content, panel by panel: People / Process / Performance
 * scores and the recommendation up top, then execution, risk, ESG and costs.
 *
 * `entityType` must be "mf" — "mutual_fund" and friends are rejected.
 */
export const MF_RESEARCH_PANELS = gql`
  query MfResearchPanels($securitySymbol: String!, $archetype: String) {
    getSecuritySymbolsResearchData(
      securitySymbol: $securitySymbol
      entityType: "mf"
      archetype: $archetype
    ) {
      success
      error
      securitySymbol
      entityType
      mfPanels {
        headerPanel {
          peopleScore
          processScore
          performanceScore
          overallRanking
          scoreOverallRationale
          recommendationAction
          recommendationRationale
          investmentHorizon
          suggestedMode
        }
      }
    }
  }
`;

/**
 * The backend's OWN head-to-head, rather than a comparison re-derived here.
 *
 * `compareSecurities` reads each side's stored research report through the same
 * resolver chain the research screens use, normalises both into one panel shape
 * and computes the winner, the per-dimension scores and the horizon fit. The
 * agent calls the identical query through `compare_securities_research`, so the
 * desk and the assistant answer "which of these two" the same way.
 *
 * `entityType` is `SecurityTypeEnum`: **mf**, `etf` or `stock`. It is NOT
 * "mutual_fund" — the enum's own comment says "MF is always `mf`" — and the
 * resolver answers a wrong value with "No research found", which reads as
 * missing data rather than as a bad argument. See `compareEntityType`.
 *
 * MODELS AND PORTFOLIOS ARE NOT SUPPORTED: the resolver branches on those three
 * types only and returns nothing for anything else, so a model has to be
 * compared through the variant's own scheme symbols or not at all.
 *
 * A side with no research is not an error — the response comes back
 * `success: true` with that panel null, and the summary says the two could not
 * be separated. Both missing is the one case that fails.
 */
export const COMPARE_SECURITIES = gql`
  query CompareSecurities(
    $symbolA: String!
    $symbolB: String!
    $entityTypeA: String!
    $entityTypeB: String!
    $horizon: String
    $archetype: String
  ) {
    compareSecurities(
      symbolA: $symbolA
      symbolB: $symbolB
      entityTypeA: $entityTypeA
      entityTypeB: $entityTypeB
      horizon: $horizon
      archetype: $archetype
    ) {
      success
      error
      symbolA
      symbolB
      horizon
      archetype
      comparison {
        winnerSymbol
        winnerRationale
        scoreA
        scoreB
        horizonFit
        recommendedFor
        dimensionScores { dimension scoreA scoreB winner rationale }
      }
      panelA { securitySymbol scoreOverall scoreOverallGrade researchCall researchCallRationale recommendationAction recommendationRationale lastResearchedAt }
      panelB { securitySymbol scoreOverall scoreOverallGrade researchCall researchCallRationale recommendationAction recommendationRationale lastResearchedAt }
    }
  }
`;
